import { CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import {
  CashOutAmount,
  EContractType,
  ESenderRole,
  TransactionStatus,
} from "../../../setup/api/services/contract/models";
import styles from "./styles.module.scss";
import { ContractContext } from "../../../providers/contract";
import UserCard from "./UserCard";
import { useTranslation } from "react-i18next";
import StatusPill from "./StatusPill";
import classNames from "classnames";
import { PrimaryButtonType } from "../../../components/common/PrimaryButton";
import ActionButton from "../../../components/common/ActionButton/ActionButton";
import NewCashoutModal from "../NewCashoutModal";
import { AlertColor } from "@mui/material";
import CustomizedSnackbars from "../../../components/common/customizedSnackbars";
import { useLocation } from "react-router-dom";
import {
  userMatchesIdentifier,
  resolveCashoutPartyRole,
} from "../../../setup/util/userIdentifier";
import { useShouldHideSettlementActionsForCashoutAmount } from "../../../hooks/useShouldHideSettlementActions";
import { IBankAddressStatus } from "../../../setup/api/services/user/models";
import { isInternationalPhoneNumber } from "../../../setup/util/internationalBank";
import { getTwoPartyCashout } from "../../../setup/util/twoPartyContract";

interface ICashoutCard {
  cashOutAmount: CashOutAmount;
  disableCashout?: boolean;
  currentPage?: number;
  hideFees?: boolean;
  bankAddressStatus?: IBankAddressStatus;
}

const cashoutButtonBaseStyle: CSSProperties = {
  maxWidth: "227px",
  height: "44px",
  border: "1px solid #6F707338",
  fontSize: "0.875rem",
  fontWeight: 500,
};

const disabledCashoutButtonStyle: CSSProperties = {
  ...cashoutButtonBaseStyle,
  backgroundColor: "transparent",
  pointerEvents: "none",
};

const CashoutCard = ({
  cashOutAmount,
  disableCashout,
  hideFees,
  bankAddressStatus,
}: ICashoutCard) => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const location = useLocation();
  const shouldHideSettlementActions =
    useShouldHideSettlementActionsForCashoutAmount(cashOutAmount);

  const [openCashoutModal, setOpenCashoutModal] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  // Check if isOperation is true in search params
  const isOperation = useMemo(() => {
    const searchParams = new URLSearchParams(location?.search);
    return searchParams?.get("isOperation") === "true";
  }, [location.search]);

  const twoPartyCashout = useMemo(
    () => getTwoPartyCashout(contract, cashOutAmount),
    [contract, cashOutAmount]
  );
  const lineFee = twoPartyCashout?.fee ?? cashOutAmount.fee;
  const linePayout = twoPartyCashout?.net ?? cashOutAmount.cashOutAmount;

  const partyUser = contract?.parties?.find((party) =>
    userMatchesIdentifier(party, cashOutAmount.userIdentification)
  );

  const user = useMemo(() => {
    if (contract?.type !== EContractType.QUICK_TRANSACTION) {
      return (
        contract?.partiesProfiles?.CUSTOMER?.find((party) =>
          userMatchesIdentifier(party, cashOutAmount.userIdentification)
        ) ||
        contract?.partiesProfiles?.PROVIDER?.find((party) =>
          userMatchesIdentifier(party, cashOutAmount.userIdentification)
        ) ||
        contract?.partiesProfiles?.BROKER?.find((party) =>
          userMatchesIdentifier(party, cashOutAmount.userIdentification)
        )
      );
    }

    if (
      userMatchesIdentifier(
        contract?.customer,
        cashOutAmount.userIdentification
      )
    ) {
      return contract?.customer;
    } else if (
      userMatchesIdentifier(
        contract?.provider,
        cashOutAmount.userIdentification
      )
    ) {
      return contract?.provider;
    } else if (partyUser) {
      return partyUser;
    }
    return contract?.provider;
  }, [
    cashOutAmount.userIdentification,
    contract?.customer,
    contract?.partiesProfiles?.BROKER,
    contract?.partiesProfiles?.CUSTOMER,
    contract?.partiesProfiles?.PROVIDER,
    contract?.provider,
    contract?.type,
    partyUser,
  ]);

  const userType = useMemo(
    () => resolveCashoutPartyRole(contract, cashOutAmount.userIdentification),
    [contract, cashOutAmount.userIdentification]
  );

  const userTypeText = useMemo(() => {
    if (userType === ESenderRole.CUSTOMER) {
      return t("lbl_the_buyer");
    } else if (userType === ESenderRole.PROVIDER) {
      return t("lbl_the_seller");
    } else if (userType === ESenderRole.BROKER) {
      return t("lbl_brokers");
    } else if (userType === ESenderRole.SERVICE_PROVIDER) {
      return t("lbl_shipping_company");
    }
    return "";
  }, [t, userType]);

  const hasManualWithdraw = useMemo(() => {
    return !!cashOutAmount?.manualWithDrawCashOut;
  }, [cashOutAmount]);

  const hasPendingMultiCashOut = useMemo(() => {
    return !!cashOutAmount?.multiCashOut;
  }, [cashOutAmount]);

  const userLastTransaction = useMemo(() => {
    const userTransactions = contract?.transactions.filter((transaction) => {
      const matchesIdentifier = userMatchesIdentifier(
        user,
        transaction.beneficiaryId
      );

      if (userType === ESenderRole.CUSTOMER) {
        return (
          matchesIdentifier &&
          (transaction.type === "CASH_OUT" ||
            transaction.type === "REFUND_CUSTOMER")
        );
      }

      return (
        matchesIdentifier &&
        (transaction.type === "CASH_OUT" ||
          transaction.type === "RELEASE_TO_PROVIDER" ||
          transaction.type === "CLOSE_RELEASE_TO_PROVIDER")
      );
    });

    if (userTransactions?.length) {
      const sortedTransactions = userTransactions.sort((a, b) => {
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
      return sortedTransactions[userTransactions.length - 1];
    }
    return null;
  }, [contract?.transactions, user, userType]);

  const isLastTransactionPendingOrFailed = useMemo(() => {
    return userLastTransaction && userLastTransaction.status === "PENDING";
  }, [userLastTransaction]);

  const disableButton = useMemo(() => {
    return isLastTransactionPendingOrFailed || disableCashout;
  }, [isLastTransactionPendingOrFailed, disableCashout]);

  const statusBillStatus = useMemo(() => {
    if (cashOutAmount?.settlementAmountStatus === "PENDING_TRANSFER") {
      if (
        userLastTransaction?.status === TransactionStatus.PENDING ||
        userLastTransaction?.status === TransactionStatus.FAILED
      ) {
        return userLastTransaction?.status;
      }
    }
    return cashOutAmount?.settlementAmountStatus;
  }, [cashOutAmount?.settlementAmountStatus, userLastTransaction]);

  const onTransferButtonClick = () => {
    setOpenCashoutModal(true);
  };

  const isHiddenForAutoCashout = useMemo(() => {
    return (
      shouldHideSettlementActions &&
      (userType === ESenderRole.PROVIDER || userType === ESenderRole.BROKER) &&
      !hasManualWithdraw &&
      !hasPendingMultiCashOut
    );
  }, [
    shouldHideSettlementActions,
    userType,
    hasManualWithdraw,
    hasPendingMultiCashOut,
  ]);

  // Outside KSA: paid by manual bank transfer only (no SingleView) — details in the modal.
  const isInternationalParty =
    !!(partyUser as { international?: boolean } | undefined)?.international ||
    isInternationalPhoneNumber(user?.phoneNumber);

  const missingBankAddressWarning = useMemo(() => {
    if (!bankAddressStatus) {
      return null;
    }
    const { hasBankInfo, hasAddress } = bankAddressStatus;
    if (hasBankInfo && hasAddress) {
      return null;
    }
    if (!hasBankInfo && !hasAddress) {
      return t("lbl_missing_bank_info_and_address_warning");
    }
    if (!hasBankInfo) {
      return t("lbl_missing_bank_info_warning");
    }
    return t("lbl_missing_address_warning");
  }, [bankAddressStatus, t]);

  // Resolve which cashout button to show (release / manual transfer / pending),
  // or `null` when none applies. Only the release action is enabled.
  const cashoutButton = useMemo(() => {
    if (cashOutAmount?.settlementAmountStatus === "TRANSFERRED") return null;
    if (isHiddenForAutoCashout) {
      return { label: t("lbl_auto_cashout"), disabled: true };
    }
    if (!disableButton && !isOperation) {
      return hasManualWithdraw
        ? { label: t("lbl_manual_transfer"), disabled: true }
        : { label: t("lbl_release"), disabled: false };
    }
    if (hasPendingMultiCashOut) {
      return { label: t("lbl_pending_multi_cash_out"), disabled: true };
    }
    return null;
  }, [
    cashOutAmount?.settlementAmountStatus,
    isHiddenForAutoCashout,
    disableButton,
    isOperation,
    hasPendingMultiCashOut,
    t,
    hasManualWithdraw,
  ]);

  const onCloseCashoutModal = () => {
    setOpenCashoutModal(false);
  };

  const onCopy = async () => {
    try {
      await navigator?.clipboard?.writeText(userLastTransaction?.id || "");
      setSnackBarOpen(true);
      setSnackBarColor("success");
      setSnackBarTitle(t("COPIED"));
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBarColor("error");
      setSnackBarTitle(t("COPIED"));
      console.error(error);
    }
  };

  useEffect(() => {
    if (snackBarOpen) {
      setTimeout(() => {
        setSnackBarOpen(false);
        setSnackBarColor("info");
        setSnackBarTitle("");
      }, 5000);
    }
  }, [snackBarOpen]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  return (
    <div className={styles.card}>
      <div className={styles.container}>
        <div className={styles.content}>
          <UserCard
            name={`${user?.profile?.fullName || ""} ${
              contract?.type !== EContractType.QUICK_TRANSACTION ||
              !user?.profile?.fullName
                ? `(${userTypeText || ""})`
                : ""
            }`}
            type={
              contract?.type !== EContractType.QUICK_TRANSACTION ||
              !user?.profile?.fullName
                ? user?.phoneNumber || ""
                : userTypeText
            }
            image={user?.profile?.photoUrl || "/assets/images/user.png"}
            isNationalIdVerified={user?.nationalIdVerified}
            isDescriptionLtr
          />
          <StatusPill status={statusBillStatus} />
        </div>

        <div className={styles.content} style={{ alignItems: "center" }}>
          <div className={styles.amount_container}>
            <div className={styles.amount}>
              <p className={styles.amount_title}>{t("lbl_deserved_amount")}</p>
              <p className={styles.amount_description}>
                {cashOutAmount.amountDue + " " + t("lbl_saudi_currency")}
              </p>
            </div>

            {!hideFees && (
              <>
                <div className={styles.amount}>
                  <p className={styles.amount_title}>{t("SERVICE_FEE")}</p>
                  <p className={styles.amount_description}>
                    {`${
                      cashOutAmount?.feePercentage && !twoPartyCashout
                        ? `${(
                            (cashOutAmount?.feePercentage || 0) * 100
                          ).toFixed(2)}% - `
                        : ""
                    } ${lineFee} ${t("lbl_saudi_currency")}`}
                  </p>
                </div>

                <div className={styles.amount}>
                  <p className={styles.amount_title}>
                    {t("lbl_loyal_receive")}
                  </p>
                  <p
                    className={classNames(
                      styles["received_amount"],
                      styles.amount_description
                    )}>
                    {linePayout + " " + t("lbl_saudi_currency")}
                  </p>
                </div>
              </>
            )}

            {userLastTransaction && userLastTransaction.id && (
              <div className={styles.amount}>
                <p className={styles.amount_title}>
                  {t("lbl_reference_number")}
                </p>
                <div className={styles.reference_number}>
                  <p className={styles.amount_description}>
                    {userLastTransaction
                      ? userLastTransaction.id
                      : t("lbl_no_reference_number")}
                  </p>
                  <img
                    src="/assets/images/svg/copy.svg"
                    alt=""
                    onClick={onCopy}
                  />
                </div>
              </div>
            )}
          </div>

          {cashoutButton && (
            <ActionButton
              style={
                cashoutButton.disabled
                  ? disabledCashoutButtonStyle
                  : { ...cashoutButtonBaseStyle, backgroundColor: "#0C4BC4" }
              }
              buttonType={
                cashoutButton.disabled
                  ? PrimaryButtonType.DISABLED
                  : PrimaryButtonType.DEFAULT
              }
              onClick={
                cashoutButton.disabled ? undefined : onTransferButtonClick
              }>
              {cashoutButton.label}
            </ActionButton>
          )}
        </div>

        {missingBankAddressWarning && (
          <p className={styles.warning}>{missingBankAddressWarning}</p>
        )}
        {isInternationalParty && (
          <p className={styles.international}>
            {t("lbl_international_party_cashout_hint")}
          </p>
        )}
      </div>
      <NewCashoutModal
        open={openCashoutModal}
        onClose={onCloseCashoutModal}
        cashOutAmount={cashOutAmount}
        userType={userType || ESenderRole.CUSTOMER}
        user={user}
      />
      <div className={styles.snackbar}>{renderSnackBar()}</div>
    </div>
  );
};

export default CashoutCard;
